import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAxios from '../api/axios';
import useAuth from '../hooks/useAuth';
import ScoreBoard from './ScoreBoard.jsx';

const hasTimestampValue = (value) => typeof value === 'string'
  ? value.trim() !== '' && value !== '0000-00-00 00:00:00'
  : Boolean(value);

const formatDateTime = (value) => {
  if (!hasTimestampValue(value)) {
    return 'Ei tiedossa';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (started, ended) => {
  if (!hasTimestampValue(started) || !hasTimestampValue(ended)) {
    return 'Ei tiedossa';
  }

  const startedDate = new Date(started);
  const endedDate = new Date(ended);

  if (Number.isNaN(startedDate.getTime()) || Number.isNaN(endedDate.getTime())) {
    return 'Ei tiedossa';
  }

  const diffMs = endedDate.getTime() - startedDate.getTime();

  if (diffMs < 0) {
    return 'Ei tiedossa';
  }

  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours} h ${minutes} min`;
  }

  if (hours > 0) {
    return `${hours} h`;
  }

  return `${minutes} min`;
};

const isAutoConfirmed = (pair) => hasTimestampValue(pair?.autoConfirmedAt);

const getAutoConfirmedPlayerSummary = (pair) => {
  if (!pair) {
    return '';
  }

  const players = [];

  if (hasTimestampValue(pair?.user1AutoConfirmedAt)) {
    players.push(getPlayerName(pair, 1));
  }

  if (hasTimestampValue(pair?.user2AutoConfirmedAt)) {
    players.push(getPlayerName(pair, 2));
  }

  return players.join(' ja ');
};

const getPairUserID = (pair, slot) => Number(pair?.[`userID${slot}`] ?? pair?.[`metrixUserID${slot}`] ?? -1);

const getPlayerName = (pair, slot) => {
  const name = pair?.[`user${slot}`];

  if (name && String(name).trim() !== '') {
    return name;
  }

  const userID = getPairUserID(pair, slot);
  return userID > 0 ? `Pelaaja #${userID}` : `Kilpailija ${slot}`;
};

const getHoleResults = (pair) => (Array.isArray(pair?.holeResults) ? pair.holeResults : []);

const calculatePoints = (pair) => {
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);

  return getHoleResults(pair).reduce((accumulator, entry) => {
    const winnerUserID = Number(entry?.winnerUserID ?? -1);

    if (winnerUserID === player1UserID) {
      return { ...accumulator, player1: accumulator.player1 + 1 };
    }

    if (winnerUserID === player2UserID) {
      return { ...accumulator, player2: accumulator.player2 + 1 };
    }

    return accumulator;
  }, { player1: 0, player2: 0 });
};

const buildSummaryRows = (pair) => {
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);
  let player1Points = 0;
  let player2Points = 0;

  return getHoleResults(pair).map((entry) => {
    const winnerUserID = Number(entry?.winnerUserID ?? -1);

    if (winnerUserID === player1UserID) {
      player1Points += 1;
    }

    if (winnerUserID === player2UserID) {
      player2Points += 1;
    }

    return {
      hole: entry?.hole,
      holeLabel: entry?.hole === 'SD' ? 'Suddendeath' : `Väylä ${entry?.hole}`,
      winnerUserID,
      player1Points,
      player2Points,
    };
  });
};

const getSummaryWinnerText = (pair) => {
  const player1Name = getPlayerName(pair, 1);
  const player2Name = getPlayerName(pair, 2);
  const winnerUserID = Number(pair?.winnerUserID ?? 0);
  const player1UserID = getPairUserID(pair, 1);
  const player2UserID = getPairUserID(pair, 2);
  const surrenderedByUserID = Number(pair?.surrenderedByUserID ?? 0);

  if (winnerUserID === player1UserID) {
    return surrenderedByUserID > 0 ? `${player1Name} voitti ottelun luovutuksella` : `${player1Name} voitti ottelun`;
  }

  if (winnerUserID === player2UserID) {
    return surrenderedByUserID > 0 ? `${player2Name} voitti ottelun luovutuksella` : `${player2Name} voitti ottelun`;
  }

  return 'Ottelu kesken';
};

function Results() {
  const { competitionID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useAxios();
  const { auth } = useAuth();
  const initialCompetition = location.state?.competition ?? null;
  const initialRegisteredClassID = location.state?.registeredClassID ?? null;
  const returnTo = location.state?.returnTo ?? null;
  const bracketFocusTargets = Array.isArray(location.state?.bracketFocusTargets)
    ? location.state.bracketFocusTargets
    : [];
  const isAuthenticated = Boolean(auth?.user);

  const [competition, setCompetition] = useState(initialCompetition);
  const [registeredClassID, setRegisteredClassID] = useState(initialRegisteredClassID);
  const [selectedClassID, setSelectedClassID] = useState(initialRegisteredClassID);
  const [pairs, setPairs] = useState([]);
  const [loadingCompetition, setLoadingCompetition] = useState(!initialCompetition);
  const [loadingPairs, setLoadingPairs] = useState(true);
  const [error, setError] = useState('');
  const [selectedPair, setSelectedPair] = useState(null);

  useEffect(() => {
    if (initialCompetition) {
      return;
    }

    let cancelled = false;

    const loadCompetition = async () => {
      setLoadingCompetition(true);
      try {
        const response = await api.post(
          '/getCompetitions',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );

        const competitions = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.competitions)
            ? response.data.competitions
            : [];

        const currentCompetition = competitions.find(
          (item) => Number(item.competitionID) === Number(competitionID)
        ) || null;

        if (!cancelled) {
          setCompetition(currentCompetition);
        }
      } catch {
        if (!cancelled) {
          setError('Kilpailun tietojen haku epäonnistui.');
        }
      } finally {
        if (!cancelled) {
          setLoadingCompetition(false);
        }
      }
    };

    loadCompetition();

    return () => {
      cancelled = true;
    };
  }, [competitionID, initialCompetition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated || initialRegisteredClassID !== null) {
      return;
    }

    let cancelled = false;

    const loadRegistration = async () => {
      try {
        const response = await api.post(
          '/getMyRegistrations',
          JSON.stringify({}),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!Array.isArray(response?.data) || cancelled) {
          return;
        }

        const registration = response.data.find(
          (item) => Number(item?.competitionID) === Number(competitionID)
        );

        const classID = registration?.classID ?? null;
        setRegisteredClassID(classID);
        setSelectedClassID((prev) => (prev === null || prev === undefined ? classID : prev));
      } catch {
        // non-critical
      }
    };

    loadRegistration();

    return () => {
      cancelled = true;
    };
  }, [competitionID, initialRegisteredClassID, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useMemo(() => (Array.isArray(competition?.classes) ? competition.classes : []), [competition]);

  useEffect(() => {
    if (selectedClassID !== null && selectedClassID !== undefined) {
      return;
    }

    if (registeredClassID !== null && registeredClassID !== undefined) {
      setSelectedClassID(registeredClassID);
      return;
    }

    if (classes.length > 0) {
      setSelectedClassID(Number(classes[0].classID));
    }
  }, [classes, registeredClassID, selectedClassID]);

  useEffect(() => {
    if (!competitionID) {
      return;
    }

    let cancelled = false;

    const loadPairs = async () => {
      setLoadingPairs(true);
      setError('');
      try {
        const payload = {
          competitionID: Number(competitionID),
          ...(selectedClassID !== null && selectedClassID !== undefined ? { classID: Number(selectedClassID) } : {}),
        };

        const response = await api.post(
          '/getPairs',
          JSON.stringify(payload),
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (!cancelled) {
          setPairs(Array.isArray(response?.data) ? response.data : []);
        }
      } catch {
        if (!cancelled) {
          setPairs([]);
          setError('Parien haku epäonnistui.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPairs(false);
        }
      }
    };

    loadPairs();

    return () => {
      cancelled = true;
    };
  }, [competitionID, selectedClassID]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedClass = useMemo(() => {
    if (!classes.length) {
      return null;
    }

    return classes.find((cls) => Number(cls.classID) === Number(selectedClassID)) ?? null;
  }, [classes, selectedClassID]);

  const currentUserID = useMemo(() => {
    const possibleUserID = auth?.user?.userID
      ?? auth?.user?.ID
      ?? auth?.user?.userObj?.userID
      ?? auth?.user?.userObj?.ID
      ?? null;

    return possibleUserID !== null && possibleUserID !== undefined
      ? Number(possibleUserID)
      : null;
  }, [auth]);

  const selectedPairPoints = useMemo(() => (selectedPair ? calculatePoints(selectedPair) : { player1: 0, player2: 0 }), [selectedPair]);
  const selectedPairRows = useMemo(() => (selectedPair ? buildSummaryRows(selectedPair) : []), [selectedPair]);
  const selectedPairPlayer1Name = useMemo(() => getPlayerName(selectedPair, 1), [selectedPair]);
  const selectedPairPlayer2Name = useMemo(() => getPlayerName(selectedPair, 2), [selectedPair]);
  const selectedPairPlayer1UserID = useMemo(() => getPairUserID(selectedPair, 1), [selectedPair]);
  const selectedPairPlayer2UserID = useMemo(() => getPairUserID(selectedPair, 2), [selectedPair]);
  const selectedPairDuration = useMemo(() => formatDuration(selectedPair?.started, selectedPair?.ended), [selectedPair]);

  const handleBackToCompetition = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }

    navigate('/kisat');
  };

  return (
    <section className="px-4 pb-16 pt-24">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 px-6 py-6 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Tulokset</p>
              <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">
                {competition?.name || 'Kilpailun tulokset'}
              </h1>
            </div>

            <div className="md:shrink-0">
              <button
                type="button"
                onClick={handleBackToCompetition}
                className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Takaisin kisaan
              </button>
            </div>
          </div>

          <div className="mt-5">
            {loadingCompetition ? (
              <p className="text-sm text-slate-300">Ladataan kilpailua...</p>
            ) : classes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {classes.map((cls) => {
                  const isActive = Number(cls.classID) === Number(selectedClassID);
                  return (
                    <button
                      key={cls.classID}
                      type="button"
                      onClick={() => setSelectedClassID(Number(cls.classID))}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                        isActive
                          ? 'border-cyan-500 bg-cyan-600 text-white'
                          : 'border-white/15 bg-white/5 text-slate-200 hover:bg-white/10'
                      }`}
                    >
                      {cls.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-300">Kilpailulle ei ole määritelty luokkia.</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          {error ? (
            <div className="rounded-3xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700">
              {error}
            </div>
          ) : (
            <ScoreBoard
              pairs={pairs}
              loading={loadingPairs}
              className={selectedClass?.name ?? ''}
              competitionName={competition?.name ?? ''}
              currentUserID={currentUserID}
              onSelectPair={setSelectedPair}
              competitionStartDate={competition?.startDate ?? competition?.startdate ?? competition?.start_date ?? null}
              roundDurationDays={competition?.roundtime ?? competition?.roundTime ?? null}
              bracketFocusTargets={bracketFocusTargets}
            />
          )}
        </div>

        {selectedPair ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4" onClick={() => setSelectedPair(null)}>
            <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-40px_rgba(15,23,42,0.45)]" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-600">Ottelun yhteenveto</p>
                  <h2 className="mt-2 text-3xl font-black text-slate-900">{getSummaryWinnerText(selectedPair)}</h2>
                  <p className="mt-3 text-lg font-semibold text-slate-700">{selectedPairPlayer1Name} {selectedPairPoints.player1} - {selectedPairPoints.player2} {selectedPairPlayer2Name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPair(null)}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  Sulje
                </button>
              </div>

              <div className="mt-6 rounded-[28px] border border-slate-200 bg-slate-50 px-5 py-5">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Ottelun tiedot</p>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Alkanut</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(selectedPair?.started)}</p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Päättynyt</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{formatDateTime(selectedPair?.ended)}</p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Kesto</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">{selectedPairDuration}</p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Vahvistus</p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">
                      {isAutoConfirmed(selectedPair) ? `Automaattinen: ${getAutoConfirmedPlayerSummary(selectedPair) || 'järjestelmä'} (${formatDateTime(selectedPair?.autoConfirmedAt)})` : 'Pelaajien vahvistama'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[28px] border border-slate-200 bg-slate-50 px-5 py-5">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Väylätulokset</p>
                {selectedPairRows.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {selectedPairRows.map((entry) => {
                      const isPlayer1Winner = entry.winnerUserID === selectedPairPlayer1UserID;
                      const isPlayer2Winner = entry.winnerUserID === selectedPairPlayer2UserID;

                      return (
                        <div key={`${entry.hole}-${entry.winnerUserID}`} className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                          <div className="grid gap-2 sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center sm:gap-3">
                            <span className="font-semibold text-slate-900">{entry.holeLabel}</span>
                            <span className={`rounded-md px-2 py-1 font-semibold sm:justify-self-end ${isPlayer1Winner ? 'bg-emerald-200 text-emerald-950' : 'text-slate-700'}`}>
                              {selectedPairPlayer1Name}
                            </span>
                            <span className="font-bold text-slate-500 sm:justify-self-center">{entry.player1Points} - {entry.player2Points}</span>
                            <span className={`rounded-md px-2 py-1 font-semibold sm:justify-self-start ${isPlayer2Winner ? 'bg-emerald-200 text-emerald-950' : 'text-slate-700'}`}>
                              {selectedPairPlayer2Name}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-slate-500">Tälle ottelulle ei ole vielä väylätuloksia.</p>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default Results;
